import { h, Host } from '@stencil/core';
import cn from 'classnames';
import { send, rangeTabClickEvent, setArticleTitle, rangeTabs, TAB_NAMES } from '@pes-ui/utils';
import isEmpty from 'lodash/isEmpty';
import { scrollToTop } from '../../../globals/utils/helpers';
export class PesTabs {
  constructor() {
    this.previousTabHash = '';
    this.previousListener = null;
    this.handleOverviewNavigation = () => this.handleTabClick(this.overviewTab);
    this.setExpectedActiveTab = () => {
      var _a, _b;
      const currentHash = (_b = (_a = window.location.hash) === null || _a === void 0 ? void 0 : _a.replace) === null || _b === void 0 ? void 0 : _b.call(_a, '#', '');
      if (!Object.values(TAB_NAMES).includes(currentHash))
        return;
      if (this.activeTabHash !== currentHash) {
        setArticleTitle(rangeTabs[currentHash]);
        this.activeTabHash = currentHash;
        scrollToTop(this.hostElementRef);
      }
    };
    this.setLocationHash = (hash) => {
      const { currentPath, currentState } = this.getRouteDetails();
      if (!hash) {
        window.history.pushState(currentState, document.title, currentPath);
        return;
      }
      window.history.pushState(currentState, document.title, `${currentPath}#${hash}`);
    };
    this.handleTabClick = (tab) => {
      if (this.activeTabHash !== tab.hash) {
        setArticleTitle(rangeTabs[tab.hash]);
        send(rangeTabClickEvent, { tabHash: tab.hash });
        this.previousTabHash = this.activeTabHash;
      }
      this.setLocationHash(tab.hideHash ? undefined : tab.hash);
      this.activeTabHash = tab.hash;
      scrollToTop(this.hostElementRef);
    };
    this.tabs = [];
    this.desktopOnly = false;
    this.activeTabHash = this.setActiveFirstTab();
    this.overviewTab = null;
  }
  connectedCallback() {
    this.setTabFromHash();
    document.addEventListener('activeOverview', this.handleOverviewNavigation);
    this.previousListener = window.onhashchange;
    const componentScope = this;
    window.onhashchange = function (...params) {
      var _a, _b;
      const result = (_b = (_a = componentScope.previousListener) === null || _a === void 0 ? void 0 : _a.apply) === null || _b === void 0 ? void 0 : _b.call(_a, this, params);
      componentScope.setExpectedActiveTab();
      return result;
    };
  }
  disconnectedCallback() {
    document.removeEventListener('activeOverview', this.handleOverviewNavigation);
    window.onhashchange = this.previousListener;
  }
  setActiveFirstTab() {
    if (this.tabs.length) {
      return this.tabs[0].hash;
    }
    return 'main';
  }
  setTabFromHash() {
    const currentHash = window.location.hash.substring(1);
    const currentTab = this.tabs.find((tab) => {
      return currentHash === tab.hash;
    });
    if (isEmpty(currentTab))
      return;
    this.activeTabHash = currentTab.hash;
  }
  getRouteDetails() {
    return {
      currentState: window.history.state || null,
      currentPath: window.location.pathname + window.location.search,
    };
  }
  componentDidUpdate() {
    if (this.activeTabHash === this.previousTabHash)
      return;
    this.previousTabHash = this.activeTabHash;
    window.dispatchEvent(new Event('resize'));
  }
  componentWillLoad() {
    var _a;
    if (!window.location.hash) {
      this.setLocationHash(this.activeTabHash);
    }
    this.overviewTab = (_a = this.tabs) === null || _a === void 0 ? void 0 : _a.find(({ hash }) => hash.includes(TAB_NAMES.Presentation));
  }
  renderTabs() {
    return (h("nav", { dir: document.documentElement.dir || 'auto' }, this.tabs.map((tab) => (h("a", { onClick: () => this.handleTabClick(tab), class: cn({
        active: tab.hash === this.activeTabHash,
        'pes-tabs-hidden': tab.hidden && tab.hash !== this.activeTabHash,
      }) }, h("h2", { class: "pes-tabs__title" }, tab.title))))));
  }
  render() {
    if (!this.tabs.length)
      return null;
    return (h(Host, null, h("se-container", { option: "centered", class: cn('pes-tabs', { 'pes-tabs--desktop-only': this.desktopOnly }) }, h("se-tabbar", { option: "content" }, this.renderTabs())), h("se-container", { option: "centered", class: "pes-tabs-content", color: "standard" }, this.tabs.map(({ renderFunction, slotName, hash, hidden }) => {
      return (h("div", { key: hash, class: cn({
          'pes-tabs-content-hidden': this.activeTabHash !== hash || hidden,
        }) }, renderFunction(slotName ? h("slot", { name: slotName }) : undefined)));
    }))));
  }
  static get is() { return "pes-tabs"; }
  static get originalStyleUrls() {
    return {
      "$": ["tabs.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["tabs.css"]
    };
  }
  static get properties() {
    return {
      "tabs": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TTab[]",
          "resolved": "TTab[]",
          "references": {
            "TTab": {
              "location": "import",
              "path": "./types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Array of tabs"
        },
        "defaultValue": "[]"
      },
      "desktopOnly": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Visible only for the desktop view"
        },
        "attribute": "desktop-only",
        "reflect": false,
        "defaultValue": "false"
      }
    };
  }
  static get states() {
    return {
      "activeTabHash": {},
      "overviewTab": {}
    };
  }
  static get elementRef() { return "hostElementRef"; }
}
